<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  applicantNames: string
  generatedAt: string
  pageNumber: number
  totalPages: number
}>()

const { locale, t } = useI18n()
const generatedDate = computed(() =>
  new Intl.DateTimeFormat(locale.value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  }).format(new Date(props.generatedAt)),
)
const generatedDateTime = computed(() =>
  new Intl.DateTimeFormat(locale.value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  }).format(new Date(props.generatedAt)),
)
</script>

<template>
  <section class="self-disclosure-page">
    <header class="page-header">
      <img src="@assets/images/logo.svg" alt="FinEstHub" />
      <div>
        <strong>{{ t('title') }}</strong>
        <span v-if="applicantNames">{{ applicantNames }}</span>
        <span>{{ t('date') }}: {{ generatedDate }}</span>
      </div>
    </header>

    <main><slot /></main>

    <footer class="page-footer">
      <span>{{ generatedDateTime }}</span>
      <span>www.finesthub.de</span>
      <span>{{ pageNumber }} / {{ totalPages }}</span>
    </footer>
  </section>
</template>

<style scoped>
.self-disclosure-page {
  position: relative;
  width: 210mm;
  height: 296mm;
  box-sizing: border-box;
  padding: 10mm;
  overflow: hidden;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 20mm;
  margin-bottom: 8mm;
  border-bottom: 0.25mm solid var(--color-surface-900);
  padding-bottom: 4mm;
}
.page-header img {
  width: 58mm;
  height: auto;
}
.page-header div {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-family: Roboto, sans-serif;
  font-size: 8pt;
  line-height: 1.2;
}
.page-header strong {
  font-size: 11pt;
}
main {
  max-height: 235mm;
}
.page-footer {
  position: absolute;
  right: 10mm;
  bottom: 7mm;
  left: 10mm;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-top: 0.25mm solid var(--color-surface-900);
  padding-top: 5mm;
  font-family: Roboto, sans-serif;
  font-size: 7pt;
  color: var(--color-surface-900);
}
.page-footer span:nth-child(2) {
  text-align: center;
}
.page-footer span:last-child {
  text-align: right;
}
@media screen {
  .self-disclosure-page {
    margin-bottom: 3rem;
    border-bottom: 1px dashed var(--color-surface-300);
  }
}
</style>

<i18n>
{
  "de": { "title": "Selbstauskunft", "date": "Datum" },
  "en": { "title": "Self-disclosure", "date": "Date" }
}
</i18n>
